import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { DropzoneOptions } from 'react-dropzone';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Dropzone } from '../../../../../CommonComponents/OPL/Dropzone/Dropzone';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { toast } from '../../../../../CommonComponents/OPL/Toast/Toast';
import { useUploadLogoMutation } from './api/useUploadLogoMutation';
import { LogoInfo } from './components/LogoInfo/LogoInfo';
import { Loading } from '../../../../../CommonComponents/OPL/Loading/Loading';
import { LogoPreview } from './components/LogoPreview/LogoPreview';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { MarginProps } from '../../../../../theme/styledProps';
import { LOGO_CONTAINER_HEIGHT } from './FirmLogo.meta';
import { FormFieldName as FirmFormFieldName } from '../../FirmSettings.meta';
import { FormFieldName as MySettingsFormFieldName } from '../../../MySettings/MySettings.meta';

interface Props extends MarginProps {
  isFirm?: boolean;
  formBag: UseFormReturn<any>;
  customFirmUrl: string;
  fieldName: FirmFormFieldName | MySettingsFormFieldName;
}

export const FirmLogo: React.FC<Props> = ({
  formBag,
  fieldName,
  customFirmUrl,
  isFirm,
  ...rest
}) => {
  const { mutateAsync: uploadLogo, isLoading: isUploadingLogo } = useUploadLogoMutation();

  const firmLogoFilename = formBag.watch(fieldName);

  const dropZoneOptions: DropzoneOptions = {
    noClick: true,
    accept: ['image/png', 'image/jpeg', 'image/jpg'],
    multiple: false,
    onDropAccepted: async ([file]) => {
      try {
        const fileName = await uploadLogo({ file });

        formBag.setValue(fieldName, fileName, { shouldDirty: true });
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err.message);
        }
      }
    },
    onDropRejected() {
      toast.error('Upload an image in .png, .jpeg or .jpg format.');
    },
  };

  return (
    <Box as="section" {...rest}>
      <SectionHeader title="Firm Logo" />

      <Box display="flex" gridGap="16" alignItems="stretch">
        <Box flex={1} position="relative">
          <Controller
            control={formBag.control}
            name={fieldName}
            render={() =>
              isUploadingLogo ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height={LOGO_CONTAINER_HEIGHT}
                >
                  <Loading text="Uploading..." />
                </Box>
              ) : firmLogoFilename ? (
                <LogoPreview
                  fieldName={fieldName}
                  firmLogoFilename={firmLogoFilename}
                  dropZoneOptions={dropZoneOptions}
                  formBag={formBag}
                />
              ) : (
                <Dropzone options={dropZoneOptions} />
              )
            }
          />
        </Box>

        <Box flex={1}>
          <Alert
            variant="info"
            title="Logo Specifications"
            message={<LogoInfo customFirmUrl={customFirmUrl} />}
            scrollIntoView={false}
            isFullHeight
          />
        </Box>
      </Box>

      {isFirm && (
        <Toggle {...formBag.register('allowAdvisersOverrideFirmLogo')} mt="24">
          Allow advisors to override Firm Logo
        </Toggle>
      )}
    </Box>
  );
};
